import { useHotkeys } from 'react-hotkeys-hook';
import invariant from 'tiny-invariant';

import { usePlayerStore } from '../components/PlayerContextProvider';
import { usePlayerVolumeChange } from './usePlayerVolumeChange';

export type UsePlayerVolumeShortcutParams = {
  /**
   * The scope for the volume shortcut.
   * @see https://github.com/JohannesKlauss/react-hotkeys-hook?tab=readme-ov-file#scopes
   */
  scope: string;
  /**
   * The step size for the volume shortcut.
   * @default 0.1 (10%)
   */
  step?: number;
};

export const usePlayerVolumeShortcut = ({ scope, step = 0.1 }: UsePlayerVolumeShortcutParams) => {
  const initialized = usePlayerStore((store) => store.initialized);
  const { onPlayerVolumeDecrease, onPlayerVolumeIncrease } = usePlayerVolumeChange();

  invariant(step > 0 && step <= 1, 'Step must be between 0 and 1');

  useHotkeys(
    'up',
    () => {
      onPlayerVolumeIncrease(step);
    },
    {
      description: `Increase volume by ${step * 100}%`,
      enabled: initialized,
      preventDefault: true,
      scopes: [scope],
    },
    [onPlayerVolumeIncrease, scope, step],
  );

  useHotkeys(
    'down',
    () => {
      onPlayerVolumeDecrease(step);
    },
    {
      description: `Decrease volume by ${step * 100}%`,
      enabled: initialized,
      preventDefault: true,
      scopes: [scope],
    },
    [onPlayerVolumeDecrease, scope, step],
  );
};
